import { useQuery } from "@tanstack/react-query";
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { Can } from "../../access/Can";
import { operatorsUsageMonthlyQueryOptions } from "../../lib/operatorsReactQuery";
import {
  type OperatorsUsageMonthlyModelSummary,
  type OperatorsUsageMonthlyUserSummary,
} from "../../operators";
import { Button } from "../ui/button";

import { SettingsPageHeader, SettingsPanel, SettingsSection } from "./SettingsChrome";

const currentMonthKey = (): string => {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
};

const shiftMonth = (monthKey: string, delta: number): string => {
  const [yearPart, monthPart] = monthKey.split("-");
  const date = new Date(Date.UTC(Number(yearPart), Number(monthPart) - 1 + delta, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
};

const monthLabel = (month: string): string => {
  const [yearPart, monthPart] = month.split("-");
  const date = new Date(Date.UTC(Number(yearPart), Number(monthPart) - 1, 1));
  return new Intl.DateTimeFormat(undefined, {
    month: "long",
    timeZone: "UTC",
    year: "numeric",
  }).format(date);
};

const compactNumberFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 1,
  notation: "compact",
});

const integerFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 0,
});

const formatCompact = (value: number): string => compactNumberFormatter.format(value);

const formatInteger = (value: number): string => integerFormatter.format(value);

const uncachedInputTokens = (inputTokens: number, cachedInputTokens: number): number =>
  Math.max(0, inputTokens - cachedInputTokens);

const formatTokenBreakdown = (
  inputTokens: number,
  cachedInputTokens: number,
  outputTokens: number,
): string =>
  `${formatCompact(uncachedInputTokens(inputTokens, cachedInputTokens))} uncached input, ${formatCompact(cachedInputTokens)} cached input, ${formatCompact(outputTokens)} output`;

const userDisplayName = (user: OperatorsUsageMonthlyUserSummary): string =>
  user.userName ?? user.userEmail ?? "Unattributed";

const userDetail = (user: OperatorsUsageMonthlyUserSummary): string =>
  user.userEmail ?? "No preceding user message";

const userInitials = (user: OperatorsUsageMonthlyUserSummary): string => {
  const label = userDisplayName(user);
  const initials = label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
  return initials || "UN";
};

const usageShare = (value: number, total: number): number => {
  if (total <= 0 || value <= 0) {
    return 0;
  }
  return Math.max(2, Math.min(100, (value / total) * 100));
};

function SummaryMetric({ label, note, value }: { label: string; note: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className="mt-2 text-2xl font-semibold tabular-nums tracking-tight text-foreground">
        {value}
      </p>
      <p className="mt-2 text-xs text-muted-foreground">{note}</p>
    </div>
  );
}

function EmptyTableState({ label }: { label: string }) {
  return (
    <div className="px-5 py-10 text-center text-sm text-muted-foreground">
      No usage recorded for {label}.
    </div>
  );
}

function UserUsageTable({
  totalTokens,
  users,
}: {
  totalTokens: number;
  users: OperatorsUsageMonthlyUserSummary[];
}) {
  if (users.length === 0) {
    return null;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[980px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/35 text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            <th className="px-5 py-3">User</th>
            <th className="px-5 py-3 text-right">Threads</th>
            <th className="px-5 py-3 text-right">Uncached input tokens</th>
            <th className="px-5 py-3 text-right">Cached input tokens</th>
            <th className="px-5 py-3 text-right">Output tokens</th>
            <th className="px-5 py-3 text-right">Total tokens</th>
            <th className="px-5 py-3">Share</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            return (
              <tr
                key={user.userId ?? user.userEmail ?? "unattributed"}
                className="border-b border-border last:border-b-0"
              >
                <td className="px-5 py-3">
                  <div className="flex min-w-[190px] items-center gap-3">
                    <div className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary/8 text-xs font-semibold text-primary">
                      {userInitials(user)}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate font-medium text-foreground">
                        {userDisplayName(user)}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">{userDetail(user)}</p>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-3 text-right tabular-nums">{formatInteger(user.threads)}</td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {formatCompact(uncachedInputTokens(user.inputTokens, user.cachedInputTokens))}
                </td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {formatCompact(user.cachedInputTokens)}
                </td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {formatCompact(user.outputTokens)}
                </td>
                <td className="px-5 py-3 text-right tabular-nums">
                  {formatCompact(user.totalTokens)}
                </td>
                <td className="px-5 py-3">
                  <div className="h-2 w-36 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${usageShare(user.totalTokens, totalTokens)}%` }}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ModelUsageList({
  models,
  totalTokens,
}: {
  models: OperatorsUsageMonthlyModelSummary[];
  totalTokens: number;
}) {
  if (models.length === 0) {
    return null;
  }

  return (
    <div className="grid gap-3 p-5">
      {models.map((model) => {
        return (
          <div key={model.model} className="grid gap-2">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{model.model}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {formatTokenBreakdown(
                    model.inputTokens,
                    model.cachedInputTokens,
                    model.outputTokens,
                  )}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <strong className="block text-sm tabular-nums">
                  {formatCompact(model.totalTokens)} tokens
                </strong>
              </div>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${usageShare(model.totalTokens, totalTokens)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function UsageSettingsPanel() {
  const [selectedMonth, setSelectedMonth] = useState(() => currentMonthKey());
  const [currentMonth, setCurrentMonth] = useState(() => currentMonthKey());
  const usageQuery = useQuery(operatorsUsageMonthlyQueryOptions({ month: selectedMonth }));
  const label = monthLabel(selectedMonth);
  const usage = usageQuery.data;
  const showUsageContent = !usageQuery.isError;
  const nextDisabled = selectedMonth >= currentMonth;
  const errorMessage =
    usageQuery.error instanceof Error
      ? usageQuery.error.message
      : "Could not load usage analytics.";

  useEffect(() => {
    const interval = window.setInterval(() => setCurrentMonth(currentMonthKey()), 60_000);
    return () => window.clearInterval(interval);
  }, []);

  return (
    <SettingsPanel>
      <SettingsPageHeader
        title="Usage"
        description="Monthly OpenAI token usage attributed to the operator who last sent a user message."
        actions={
          <>
            <Button
              size="icon-sm"
              variant="outline"
              aria-label="Previous month"
              onClick={() => setSelectedMonth((month) => shiftMonth(month, -1))}
            >
              <ChevronLeftIcon className="size-4" />
            </Button>
            <div className="flex h-8 min-w-44 items-center justify-center gap-2 rounded-lg border border-border bg-background px-3 text-sm font-medium">
              <CalendarDaysIcon className="size-4 text-muted-foreground" />
              {label}
            </div>
            <Button
              size="icon-sm"
              variant="outline"
              aria-label="Next month"
              disabled={nextDisabled}
              onClick={() => setSelectedMonth((month) => shiftMonth(month, 1))}
            >
              <ChevronRightIcon className="size-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={selectedMonth === currentMonth}
              onClick={() => setSelectedMonth(currentMonth)}
            >
              Current month
            </Button>
          </>
        }
      />

      {usageQuery.isError ? (
        <section className="rounded-xl border border-destructive/30 bg-destructive/6 p-4 text-sm text-destructive">
          {errorMessage}
        </section>
      ) : null}

      {showUsageContent ? (
        <>
          <SettingsSection title="Monthly summary" description="Totals for the selected month.">
            <div className="grid gap-3 md:grid-cols-2" aria-label="Monthly summary">
              <SummaryMetric
                label="Threads"
                value={usageQuery.isLoading ? "..." : formatInteger(usage?.threadCount ?? 0)}
                note="With usage during the selected month"
              />
              <SummaryMetric
                label="Total tokens"
                value={usageQuery.isLoading ? "..." : formatCompact(usage?.totalTokens ?? 0)}
                note={formatTokenBreakdown(
                  usage?.inputTokens ?? 0,
                  usage?.cachedInputTokens ?? 0,
                  usage?.outputTokens ?? 0,
                )}
              />
            </div>
          </SettingsSection>

          <SettingsSection
            flush
            title="Usage by user"
            description="Assistant usage stays with that user until another user message appears."
          >
            <Can
              permission="usage.read_org"
              fallback={
                <div className="px-5 py-10 text-center text-sm text-muted-foreground">
                  Org-wide user breakdown requires the usage.read_org permission.
                </div>
              }
            >
              {usageQuery.isLoading ? (
                <div className="px-5 py-10 text-center text-sm text-muted-foreground">
                  Loading usage...
                </div>
              ) : usage && usage.userBreakdown.length > 0 ? (
                <UserUsageTable totalTokens={usage.totalTokens} users={usage.userBreakdown} />
              ) : (
                <EmptyTableState label={label} />
              )}
            </Can>
          </SettingsSection>

          <SettingsSection
            flush
            title="Most used models"
            description="Ranked by monthly token usage."
          >
            {usageQuery.isLoading ? (
              <div className="px-5 py-10 text-center text-sm text-muted-foreground">
                Loading models...
              </div>
            ) : usage && usage.modelBreakdown.length > 0 ? (
              <ModelUsageList models={usage.modelBreakdown} totalTokens={usage.totalTokens} />
            ) : (
              <EmptyTableState label={label} />
            )}
          </SettingsSection>
        </>
      ) : null}
    </SettingsPanel>
  );
}
